import { ComponentProps, ReactNode } from 'react';
import Typography from '@alltrails/core/components/Typography';
import Link from '@alltrails/denali/components/Link';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import AppIcon from '@alltrails/denali/components/AppIcon';
import Button from '@alltrails/denali/components/Button';
import type DeeplinkData from '@alltrails/types/DeeplinkData';
import StarRating from '@alltrails/denali/components/StarRating';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import Image, { StaticImageData } from 'next/image';
import { LanguageRegionCode } from '@alltrails/core/types/LanguageRegionCode';
import { useLanguageRegionCode } from '@alltrails/language/hooks/useLanguageRegionCode';
import AltLoginsForm from '../AltLoginsForm';
import SectionDivider from '../SectionDivider';
import TermsOfService from '../TermsOfService';
import signupModalStyles from './SignUpModal.module.scss';
import ContinueInAppButton from './ContinueInAppButton';
import styles from './InitialTreatment.module.scss';
import editorsChoiceEnUS from './images/apple-editors-choice-en-US.svg';
import editorsChoiceDaDk from './images/apple-editors-choice-da-DK.svg';
import editorsChoiceNlNl from './images/apple-editors-choice-nl-NL.svg';
import editorsChoiceEnGb from './images/apple-editors-choice-en-GB.svg';
import editorsChoiceFrFr from './images/apple-editors-choice-fr-FR.svg';
import editorsChoiceDeDe from './images/apple-editors-choice-de-DE.svg';
import editorsChoiceItIt from './images/apple-editors-choice-it-IT.svg';
import editorsChoiceNbNo from './images/apple-editors-choice-nb-NO.svg';
import editorsChoicePlPl from './images/apple-editors-choice-pl-PL.svg';
import editorsChoicePtPt from './images/apple-editors-choice-pt-PT.svg';
import editorsChoicePtBr from './images/apple-editors-choice-pt-BR.svg';
import editorsChoiceEsEs from './images/apple-editors-choice-es-ES.svg';
import editorsChoiceEsMx from './images/apple-editors-choice-es-MX.svg';
import editorsChoiceSvSE from './images/apple-editors-choice-sv-SE.svg';

type InitialProps = Pick<ComponentProps<typeof AltLoginsForm>, 'appleLoginHandler' | 'facebookLoginHandler' | 'googleLoginHandler'> & {
  heading?: ReactNode;
  onContinueWithEmailClick: () => void;
  onLogInClick: () => void;
  deeplinkData?: DeeplinkData;
  displayTrigger: CarouselDisplayTrigger;
};

const editorsChoiceImages: Record<LanguageRegionCode, StaticImageData> = {
  ['da-DK']: editorsChoiceDaDk,
  ['nl-NL']: editorsChoiceNlNl,
  ['en-GB']: editorsChoiceEnGb,
  ['en-US']: editorsChoiceEnUS,
  ['fr-FR']: editorsChoiceFrFr,
  ['de-DE']: editorsChoiceDeDe,
  ['it-IT']: editorsChoiceItIt,
  ['nb-NO']: editorsChoiceNbNo,
  ['pl-PL']: editorsChoicePlPl,
  ['pt-PT']: editorsChoicePtPt,
  ['pt-BR']: editorsChoicePtBr,
  ['es-ES']: editorsChoiceEsEs,
  ['es-MX']: editorsChoiceEsMx,
  ['sv-SE']: editorsChoiceSvSE
};

// https://www.figma.com/design/wSAbhQvyqMqrzXqrFd6Ylf/Onboarding-2025-Web--Working-?node-id=8181-291667&m=dev
export default function InitialTreatment({
  heading,
  onContinueWithEmailClick,
  onLogInClick,
  deeplinkData,
  displayTrigger,
  ...loginHandlers
}: InitialProps) {
  const languageRegionCode = useLanguageRegionCode();
  const editorsChoiceSrc = editorsChoiceImages[languageRegionCode];

  return (
    <div className={styles.content}>
      <div className={styles.container}>
        <AppIcon size={60} />
        <Typography variant="heading400">{heading || <FormattedMessage defaultMessage="AllTrails is better in the app" />}</Typography>

        <div className={styles.row}>
          <div className={styles.statsContainer}>
            <div className={styles.stats}>
              <div className={styles.rating}>
                <Typography variant="text200bold">
                  <FormattedNumber value={4.9} />
                </Typography>
                <StarRating rating={5} size="sm" />
              </div>
              <Typography color="tertiary" variant="text200">
                <FormattedMessage defaultMessage="1M+ reviews" />
              </Typography>
            </div>
          </div>
          <div className={styles.dividerContainer}>
            <div className={styles.divider} />
          </div>
          <div className={styles.award}>
            <Image src={editorsChoiceSrc} alt="" />
          </div>
        </div>
      </div>
      <div>
        <ContinueInAppButton flatColors deeplinkData={deeplinkData} displayTrigger={displayTrigger} />
        <SectionDivider classes={signupModalStyles.divider}>
          <FormattedMessage defaultMessage="or" />
        </SectionDivider>
        <AltLoginsForm {...loginHandlers} authType="signup" flatColors />
        <Button
          className={signupModalStyles.email}
          onClick={onContinueWithEmailClick}
          text={<FormattedMessage defaultMessage="Continue with email" />}
          testId="continue-with-email"
          width="full"
        />
      </div>
      <div className={styles.already}>
        <div>
          <Typography className={signupModalStyles.inline} color="secondary" component="div" variant="text200">
            <FormattedMessage defaultMessage="Already have an account?" />{' '}
          </Typography>
          <Link size="md" testId="sign-up-log-in-nav" onClick={onLogInClick}>
            <FormattedMessage defaultMessage="Log in" />
          </Link>
        </div>
        <TermsOfService />
      </div>
    </div>
  );
}
